import React, { useState, useEffect } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { UserPlus, Upload, Users, TrendingUp } from "@phosphor-icons/react";
import { CuteLoader } from "@/components/CuteLoader";

export default function AdminDashboard() {
  const [users, setUsers] = useState([]);
  const [commissionTiers, setCommissionTiers] = useState([]);
  const [payrollPeriods, setPayrollPeriods] = useState([]);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [showCreatePeriod, setShowCreatePeriod] = useState(false);
  const [showCreateTier, setShowCreateTier] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [usersRes, tiersRes, periodsRes] = await Promise.all([
        api.get("/users"),
        api.get("/commission-tiers"),
        api.get("/payroll-periods")
      ]);
      setUsers(usersRes.data);
      setCommissionTiers(tiersRes.data);
      setPayrollPeriods(periodsRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (userId, currentStatus) => {
    try {
      await api.patch(`/users/${userId}`, { is_active: !currentStatus });
      toast.success(currentStatus ? "User deactivated" : "User activated");
      loadData();
    } catch (error) {
      toast.error("Failed to update user status");
    }
  };

  const handleDeletePeriod = async (periodId) => {
    if (!window.confirm("Are you sure you want to delete this payroll period?")) {
      return;
    }
    
    try {
      await api.delete(`/payroll-periods/${periodId}`);
      toast.success("Payroll period deleted");
      loadData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Failed to delete payroll period";
      toast.error(errorMsg);
    }
  };

  const handleClearSampleData = async () => {
    if (!window.confirm("This will delete all sample payroll periods, hours, sales, tips, and training data. User accounts and commission plans will NOT be affected. Continue?")) {
      return;
    }
    
    try {
      const { data } = await api.post("/clear-sample-data");
      toast.success(`Sample data cleared: ${data.deleted.payroll_periods} periods, ${data.deleted.hours} hours, ${data.deleted.sales} sales, ${data.deleted.tips} tips, ${data.deleted.training} training records`);
      loadData();
    } catch (error) {
      toast.error("Failed to clear sample data");
    }
  };

  if (loading) {
    return <CuteLoader />;
  }

  return (
    <div className="min-h-screen bg-background p-4 md:p-8" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto space-y-8">
        <header className="flex justify-between items-center">
          <div>
            <h1 className="text-4xl sm:text-5xl font-medium tracking-tight text-foreground">Admin Dashboard</h1>
            <p className="text-base text-muted-foreground mt-2">Manage GT LAB operations</p>
          </div>
        </header>

        <Tabs defaultValue="users" className="space-y-6" data-testid="admin-tabs">
          <TabsList>
            <TabsTrigger value="users" data-testid="tab-users">Users</TabsTrigger>
            <TabsTrigger value="payroll" data-testid="tab-payroll">Payroll Periods</TabsTrigger>
            <TabsTrigger value="commission" data-testid="tab-commission">Commission Tiers</TabsTrigger>
            <TabsTrigger value="import" data-testid="tab-import">Import Data</TabsTrigger>
          </TabsList>

          <TabsContent value="users">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle className="text-2xl">User Management</CardTitle>
                    <CardDescription>Create and manage employee accounts</CardDescription>
                  </div>
                  <Dialog open={showCreateUser} onOpenChange={setShowCreateUser}>
                    <DialogTrigger asChild>
                      <Button data-testid="create-user-button">
                        <UserPlus size={20} className="mr-2" />
                        Create User
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <CreateUserForm onSuccess={() => { setShowCreateUser(false); loadData(); }} />
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Hourly Rate</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((user) => (
                      <TableRow key={user._id} data-testid={`user-row-${user._id}`}>
                        <TableCell className="font-medium">{user.name}</TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell className="capitalize">{user.role}</TableCell>
                        <TableCell>${user.hourly_rate?.toFixed(2) || "0.00"}</TableCell>
                        <TableCell>
                          <span className={`inline-flex px-2 py-1 text-xs rounded-full ${
                            user.is_active ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
                          }`}>
                            {user.is_active ? "Active" : "Inactive"}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Switch
                            checked={user.is_active}
                            onCheckedChange={() => handleToggleActive(user._id, user.is_active)}
                            data-testid={`toggle-active-${user._id}`}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="payroll">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle className="text-2xl">Payroll Periods</CardTitle>
                    <CardDescription>Manage biweekly payroll periods</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      onClick={handleClearSampleData}
                      data-testid="clear-sample-data-button"
                    >
                      Clear Sample Data
                    </Button>
                    <Dialog open={showCreatePeriod} onOpenChange={setShowCreatePeriod}>
                      <DialogTrigger asChild>
                        <Button data-testid="create-period-button">Create Period</Button>
                      </DialogTrigger>
                      <DialogContent>
                        <CreatePayrollPeriodForm onSuccess={() => { setShowCreatePeriod(false); loadData(); }} />
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {payrollPeriods.map((period) => (
                    <Card key={period._id || period.id || `${period.start_date}-${period.end_date}`} data-testid={`period-${period._id || period.id}`}>
                      <CardContent className="pt-6">
                        <div className="flex justify-between items-center">
                          <div>
                            <div className="font-medium">{period.start_date} to {period.end_date}</div>
                            <div className="text-sm text-muted-foreground mt-1">
                              Status: <span className="capitalize">{period.status}</span>
                              {period.is_sample && <span className="ml-2 text-xs bg-secondary/20 px-2 py-0.5 rounded">SAMPLE</span>}
                            </div>
                          </div>
                          <div className="flex gap-2">
                            {period.status !== "finalized" && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDeletePeriod(period._id)}
                                data-testid={`delete-period-${period._id || period.id}`}
                              >
                                Delete
                              </Button>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="commission">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle className="text-2xl">Commission Tiers</CardTitle>
                    <CardDescription>Configure commission structure</CardDescription>
                  </div>
                  <Dialog open={showCreateTier} onOpenChange={setShowCreateTier}>
                    <DialogTrigger asChild>
                      <Button data-testid="create-tier-button">Create Tier</Button>
                    </DialogTrigger>
                    <DialogContent>
                      <CreateCommissionTierForm onSuccess={() => { setShowCreateTier(false); loadData(); }} />
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Tier Name</TableHead>
                      <TableHead>Min Sales</TableHead>
                      <TableHead>Max Sales</TableHead>
                      <TableHead>Commission Rate</TableHead>
                      <TableHead>Applies to periods ending</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {commissionTiers.map((tier, idx) => (
                      <TableRow key={tier._id || tier.id || `tier-${tier.tier_name}-${idx}`} data-testid={`tier-${tier._id || tier.id || idx}`}>
                        <TableCell className="font-medium">{tier.tier_name}</TableCell>
                        <TableCell>${tier.min_sales.toFixed(2)}</TableCell>
                        <TableCell>{tier.max_sales ? `$${tier.max_sales.toFixed(2)}` : "No limit"}</TableCell>
                        <TableCell>{(tier.commission_rate * 100).toFixed(1)}%</TableCell>
                        <TableCell data-testid={`tier-range-${idx}`}>{tier.effective_from ? `from ${tier.effective_from}` : "start"}{tier.effective_to ? ` to ${tier.effective_to}` : " onward"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="import">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card data-testid="import-hours-card">
                <CardHeader>
                  <CardTitle className="text-xl">Import Hours</CardTitle>
                  <CardDescription>Upload Homebase CSV</CardDescription>
                </CardHeader>
                <CardContent>
                  <Button variant="outline" className="w-full" data-testid="upload-hours-button">
                    <Upload size={20} className="mr-2" />
                    Upload CSV
                  </Button>
                  <p className="text-xs text-muted-foreground mt-4">
                    CSV format: employee_id, date, hours, type
                  </p>
                </CardContent>
              </Card>

              <Card data-testid="import-sales-card">
                <CardHeader>
                  <CardTitle className="text-xl">Import Sales</CardTitle>
                  <CardDescription>Upload Booker CSV</CardDescription>
                </CardHeader>
                <CardContent>
                  <Button variant="outline" className="w-full" data-testid="upload-sales-button">
                    <Upload size={20} className="mr-2" />
                    Upload CSV
                  </Button>
                  <p className="text-xs text-muted-foreground mt-4">
                    CSV format: employee_id, date, service_sales, retail_sales
                  </p>
                </CardContent>
              </Card>

              <Card data-testid="import-tips-card">
                <CardHeader>
                  <CardTitle className="text-xl">Import Tips</CardTitle>
                  <CardDescription>Upload tips data CSV</CardDescription>
                </CardHeader>
                <CardContent>
                  <Button variant="outline" className="w-full" data-testid="upload-tips-button">
                    <Upload size={20} className="mr-2" />
                    Upload CSV
                  </Button>
                  <p className="text-xs text-muted-foreground mt-4">
                    CSV format: employee_id, date, amount
                  </p>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function CreateUserForm({ onSuccess }) {
  const [formData, setFormData] = useState({
    email: "",
    name: "",
    role: "employee",
    hourly_rate: "",
  });
  const [loading, setLoading] = useState(false);
  const [tempPassword, setTempPassword] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/users", {
        ...formData,
        hourly_rate: parseFloat(formData.hourly_rate),
      });
      setTempPassword(data.temporary_password);
      toast.success("User created successfully");
      setTimeout(() => {
        onSuccess();
      }, 3000);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create user");
      setLoading(false);
    }
  };

  if (tempPassword) {
    return (
      <div className="space-y-4" data-testid="temp-password-display">
        <DialogHeader>
          <DialogTitle>User Created</DialogTitle>
          <DialogDescription>Share this temporary password with the employee</DialogDescription>
        </DialogHeader>
        <div className="bg-accent p-4 rounded-md">
          <Label>Temporary Password</Label>
          <div className="font-mono text-lg mt-2 p-3 bg-background rounded border">{tempPassword}</div>
        </div>
        <p className="text-sm text-muted-foreground">
          The employee will be required to set a new password on first login.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="create-user-form">
      <DialogHeader>
        <DialogTitle>Create New User</DialogTitle>
        <DialogDescription>Add a new employee to the system</DialogDescription>
      </DialogHeader>
      <div className="space-y-2">
        <Label htmlFor="name">Full Name</Label>
        <Input
          id="name"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          required
          data-testid="user-name-input"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="email">Email</Label>
        <Input
          id="email"
          type="email"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          required
          data-testid="user-email-input"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="role">Role</Label>
        <Select value={formData.role} onValueChange={(val) => setFormData({ ...formData, role: val })}>
          <SelectTrigger data-testid="user-role-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="employee">Employee</SelectItem>
            <SelectItem value="manager">Manager</SelectItem>
            <SelectItem value="administrator">Administrator</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label htmlFor="hourly_rate">Hourly Rate</Label>
        <Input
          id="hourly_rate"
          type="number"
          step="0.01"
          value={formData.hourly_rate}
          onChange={(e) => setFormData({ ...formData, hourly_rate: e.target.value })}
          required
          data-testid="user-hourly-rate-input"
        />
      </div>
      <Button type="submit" className="w-full" disabled={loading} data-testid="submit-create-user">
        {loading ? "Creating..." : "Create User"}
      </Button>
    </form>
  );
}

function CreatePayrollPeriodForm({ onSuccess }) {
  const [formData, setFormData] = useState({ start_date: "", end_date: "" });
  const [loading, setLoading] = useState(false);
  const [manualEndDate, setManualEndDate] = useState(false);

  const handleStartDateChange = (value) => {
    setFormData({ ...formData, start_date: value });
    
    if (!manualEndDate && value) {
      // Auto-calculate end date (start + 13 days = 14-day period)
      const start = new Date(value);
      const end = new Date(start);
      end.setDate(end.getDate() + 13);
      const endDateStr = end.toISOString().split('T')[0];
      setFormData({ start_date: value, end_date: endDateStr });
    }
  };

  const handleEndDateChange = (value) => {
    setManualEndDate(true);
    setFormData({ ...formData, end_date: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate 14-day period
    if (formData.start_date && formData.end_date) {
      const start = new Date(formData.start_date);
      const end = new Date(formData.end_date);
      const days = Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1;
      
      if (days !== 14) {
        toast.error(`Payroll period must be exactly 14 days. This period is ${days} days.`);
        return;
      }
    }
    
    setLoading(true);
    try {
      await api.post("/payroll-periods", formData);
      toast.success("Payroll period created successfully");
      onSuccess();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Failed to create payroll period";
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="create-period-form">
      <DialogHeader>
        <DialogTitle>Create Payroll Period</DialogTitle>
        <DialogDescription>Define a new biweekly pay period (14 days)</DialogDescription>
      </DialogHeader>
      <div className="space-y-2">
        <Label htmlFor="start_date">Start Date</Label>
        <Input
          id="start_date"
          type="date"
          value={formData.start_date}
          onChange={(e) => handleStartDateChange(e.target.value)}
          required
          data-testid="period-start-input"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="end_date">End Date</Label>
        <Input
          id="end_date"
          type="date"
          value={formData.end_date}
          onChange={(e) => handleEndDateChange(e.target.value)}
          required
          data-testid="period-end-input"
        />
        {formData.start_date && formData.end_date && (
          <p className="text-xs text-muted-foreground">
            {(() => {
              const start = new Date(formData.start_date);
              const end = new Date(formData.end_date);
              const days = Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1;
              return days === 14 
                ? "✓ Valid 14-day biweekly period" 
                : `⚠ Warning: This period is ${days} days (expected 14)`;
            })()}
          </p>
        )}
      </div>
      <Button type="submit" className="w-full" disabled={loading} data-testid="submit-create-period">
        {loading ? "Creating..." : "Create Period"}
      </Button>
    </form>
  );
}

function CreateCommissionTierForm({ onSuccess }) {
  const [formData, setFormData] = useState({
    tier_name: "",
    min_sales: "",
    max_sales: "",
    commission_rate: "",
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/commission-tiers", {
        ...formData,
        min_sales: parseFloat(formData.min_sales),
        max_sales: formData.max_sales ? parseFloat(formData.max_sales) : null,
        commission_rate: parseFloat(formData.commission_rate) / 100,
      });
      toast.success("Commission tier created");
      onSuccess();
    } catch (error) {
      toast.error("Failed to create commission tier");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="create-tier-form">
      <DialogHeader>
        <DialogTitle>Create Commission Tier</DialogTitle>
        <DialogDescription>Define a new commission tier</DialogDescription>
      </DialogHeader>
      <div className="space-y-2">
        <Label htmlFor="tier_name">Tier Name</Label>
        <Input
          id="tier_name"
          value={formData.tier_name}
          onChange={(e) => setFormData({ ...formData, tier_name: e.target.value })}
          required
          data-testid="tier-name-input"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="min_sales">Minimum Sales ($)</Label>
        <Input
          id="min_sales"
          type="number"
          step="0.01"
          value={formData.min_sales}
          onChange={(e) => setFormData({ ...formData, min_sales: e.target.value })}
          required
          data-testid="tier-min-sales-input"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="max_sales">Maximum Sales ($) - Optional</Label>
        <Input
          id="max_sales"
          type="number"
          step="0.01"
          value={formData.max_sales}
          onChange={(e) => setFormData({ ...formData, max_sales: e.target.value })}
          data-testid="tier-max-sales-input"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="commission_rate">Commission Rate (%)</Label>
        <Input
          id="commission_rate"
          type="number"
          step="0.1"
          value={formData.commission_rate}
          onChange={(e) => setFormData({ ...formData, commission_rate: e.target.value })}
          required
          data-testid="tier-rate-input"
        />
      </div>
      <Button type="submit" className="w-full" disabled={loading} data-testid="submit-create-tier">
        {loading ? "Creating..." : "Create Tier"}
      </Button>
    </form>
  );
}